import React, { useState, useEffect } from "react";
import { Copy, Key, Eye, EyeOff, Trash2, Globe, ShieldCheck, Clock } from "lucide-react";
import ApiClient from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";

const WebhookManagement = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [showKeys, setShowKeys] = useState({});
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadApiKeys();
  }, [activeAiAgentId]);

  const loadApiKeys = async () => {
    try {
      const response = await ApiClient.request("/webhook/keys");
      setApiKeys(response.keys || []);
    } catch (error) {
      console.error("Failed to load API keys:", error);
    }
  };



  const createApiKey = async () => {
    if (!newKeyName.trim()) return;
    try {
      const response = await ApiClient.request("/webhook/keys", {
        method: "POST",
        body: JSON.stringify({ key_name: newKeyName }),
      });

      if (response.api_key) {
        showToast(`API Key "${newKeyName}" created successfully!`, "success");
      } else {
        showToast(`API Key "${newKeyName}" created successfully!`, "success");
      }

      setNewKeyName("");
      loadApiKeys();
    } catch (error) {
      showToast(`Failed: ${error.message || error}`, "error");
    }
  };

  const toggleKey = async (keyId, currentStatus) => {
    try {
      await ApiClient.request(`/webhook/keys/${keyId}`, {
        method: "PUT",
        body: JSON.stringify({ is_active: !currentStatus }),
      });
      loadApiKeys();
      showToast("Status updated");
    } catch (error) {
      showToast("Failed to toggle API key", "error");
    }
  };

  const toggleStreaming = async (keyId, currentStreamingStatus) => {
    try {
      await ApiClient.request(`/webhook/keys/${keyId}/streaming`, {
        method: "PUT",
        body: JSON.stringify({ streaming_enabled: !currentStreamingStatus }),
      });
      loadApiKeys();
      showToast("Streaming setting updated");
    } catch (error) {
      showToast("Failed to toggle streaming", "error");
    }
  };

  const toggleAutoReply = async (keyId, currentAutoReplyStatus) => {
    try {
      await ApiClient.request(`/webhook/keys/${keyId}/auto-reply`, {
        method: "PUT",
        body: JSON.stringify({ auto_reply: !currentAutoReplyStatus }),
      });
      loadApiKeys();
      showToast("Auto-reply setting updated");
    } catch (error) {
      showToast("Failed to toggle auto-reply", "error");
    }
  };

  const deleteKey = (keyId) => {
    setKeyToDelete(keyId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!keyToDelete) return;
    setIsDeleting(true);
    try {
      await ApiClient.request(`/webhook/keys/${keyToDelete}`, {
        method: "DELETE",
      });
      loadApiKeys();
      showToast("API key permanently deleted", "success");
      setIsDeleteModalOpen(false);
    } catch (error) {
      showToast("Failed to delete API key", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const copyToClipboard = (text, type = "key") => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === "url") {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      }
      const label = type === "url" ? "URL" : type === "curl" ? "cURL command" : "API Key";
      showToast(`${label} copied to clipboard!`);
    });
  };

  const webhookUrl = `${process.env.REACT_APP_API_BASE_URL}/webhook/incoming`;

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-4xl space-y-12">
        <PageHeader
          title="Neural Webhooks"
          description="Orchestrate real-time data flows between your AI ecosystem and the global digital web."
        />

        <div className="grid gap-8 animate-slideIn">
          {/* Webhook Endpoint Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-8">
              <Globe size={24} className="text-blue-600" />
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Incoming Webhook</h3>
                <p className="text-[11px] font-medium text-gray-400">Universal endpoint for external data delivery</p>
              </div>
            </div>

            <div className="p-6 bg-gray-50 rounded-[24px] border border-gray-100 group transition-all hover:bg-white hover:shadow-inner">
              <div className="flex items-center justify-between gap-4">
                <div className="font-mono text-xs text-gray-600 truncate flex-1 leading-loose">
                  {webhookUrl}
                </div>
                <button
                  onClick={() => copyToClipboard(webhookUrl, "url")}
                  className={`p-2 rounded-lg transition-all ${copiedUrl ? "text-blue-500" : "text-gray-400 hover:text-gray-900"
                    }`}
                  title={copiedUrl ? "Copied!" : "Copy URL"}
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>

            <p className="mt-4 text-[10px] text-gray-400 leading-relaxed px-2">
              All incoming POST requests to this endpoint will be processed. Ensure you include a valid `Authorization: Bearer YOUR_API_KEY` header in your requests.
            </p>


          </div>

          {/* API Key Creation Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-8">
              <Key size={24} className="text-gray-900" />
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Access Control</h3>
                <p className="text-[11px] font-medium text-gray-400">Generate secure credentials for your data streams</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="w-full flex-1 group">
                <input
                  type="text"
                  placeholder="Service Name (e.g. Stripe, Custom App)"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  className="w-full px-5 py-2.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 font-medium text-xs transition-all shadow-inner"
                  onKeyPress={(e) => e.key === "Enter" && createApiKey()}
                />
              </div>
              <button
                onClick={createApiKey}
                disabled={!newKeyName.trim()}
                className="flex items-center justify-center gap-2 px-10 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-all font-bold text-xs uppercase tracking-widest shadow-md shadow-gray-200 active:scale-95"
              >
                Create Key
              </button>
            </div>
          </div>

          {/* API Keys List Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50">
            <div className="flex items-center gap-3 mb-8">
              <ShieldCheck size={24} className="text-amber-600" />
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Credential Vault</h3>
                <p className="text-[11px] font-medium text-gray-400">{apiKeys.length} keys active in rotation</p>
              </div>
            </div>

            {apiKeys.length === 0 ? (
              <div className="text-center py-16 bg-gray-50/50 rounded-[24px] border border-gray-100 border-dashed">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-100">
                  <Key size={28} className="text-gray-300" />
                </div>
                <p className="text-gray-400 font-medium">No API keys generated yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className="group relative p-6 bg-white border border-gray-100 rounded-[24px] transition-all hover:shadow-lg hover:border-gray-200"
                  >
                    <div className="flex flex-col gap-4">
                      {/* Top Row: Name, Date, Active Toggle */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Key size={22} className={key.is_active ? "text-blue-600" : "text-gray-400"} />
                          <div>
                            <h4 className="font-bold text-gray-900">{key.key_name}</h4>
                            <div className="flex items-center gap-2 text-[10px] text-gray-400 font-bold tracking-wider uppercase">
                              <Clock size={10} />
                              {new Date(key.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        {/* Active Switch */}
                        <button
                          onClick={() => toggleKey(key.id, key.is_active)}
                          className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white flex-shrink-0 ${key.is_active ? 'border-[#83bbe1]' : 'border-gray-200'}`}
                          title={key.is_active ? "Deactivate Key" : "Activate Key"}
                        >
                          <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${key.is_active ? 'left-[22px] bg-[#83bbe1]' : 'left-[2px] bg-gray-300'}`} />
                        </button>
                      </div>

                      {/* Bottom Row: Key display and control actions */}
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pt-3 border-t border-gray-50">
                        {/* Key string box */}
                        <div className="flex-1 relative min-w-0 max-w-md w-full">
                          <div className={`font-mono text-[11px] bg-gray-50 pl-4 pr-10 py-2.5 rounded-xl border border-gray-100 text-gray-500 tracking-widest group-hover:bg-white transition-colors ${showKeys[key.id] ? "break-all" : "truncate"}`}>
                            {showKeys[key.id] ? key.api_key : "••••••••••••••••••••••••••••••••"}
                          </div>
                          <button
                            onClick={() => copyToClipboard(key.api_key, "key")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-900"
                          >
                            <Copy size={12} />
                          </button>
                        </div>

                        {/* Actions group */}
                        <div className="flex flex-wrap items-center gap-2.5">
                          <div className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-xl border border-gray-100 flex-shrink-0">
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Stream</span>
                            <button
                              onClick={() => toggleStreaming(key.id, key.streaming_enabled)}
                              className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white flex-shrink-0 ${key.streaming_enabled ? 'border-[#83bbe1]' : 'border-gray-200'}`}
                              title={key.streaming_enabled ? "Disable Streaming" : "Enable Streaming"}
                            >
                              <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${key.streaming_enabled ? 'left-[22px] bg-[#83bbe1]' : 'left-[2px] bg-gray-300'}`} />
                            </button>
                          </div>

                          <div className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-xl border border-gray-100 flex-shrink-0">
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Auto Reply</span>
                            <button
                              onClick={() => toggleAutoReply(key.id, key.auto_reply)}
                              className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white flex-shrink-0 ${key.auto_reply ? 'border-[#83bbe1]' : 'border-gray-200'}`}
                              title={key.auto_reply ? "Disable Auto-Reply" : "Enable Auto-Reply"}
                            >
                              <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${key.auto_reply ? 'left-[22px] bg-[#83bbe1]' : 'left-[2px] bg-gray-300'}`} />
                            </button>
                          </div>

                          <button
                            onClick={() => copyToClipboard(`curl -X POST "${webhookUrl}" \\\n  -H "Content-Type: application/json" \\\n  -H "Authorization: Bearer ${key.api_key}" \\\n  -d '{\n    "message": "Hello from webhook",\n    "session_id": ""\n  }'`, "curl")}
                            className="px-3 py-1.5 rounded-xl border border-gray-100 hover:border-gray-200 text-gray-400 hover:text-gray-900 hover:bg-gray-50 transition-all text-[10px] font-bold uppercase tracking-wider bg-white flex items-center gap-1.5 shadow-sm"
                            title="Copy cURL command with this key"
                          >
                            <Copy size={12} />
                            <span>cURL</span>
                          </button>
                          <button
                            onClick={() => setShowKeys(prev => ({ ...prev, [key.id]: !prev[key.id] }))}
                            className={`p-2.5 rounded-xl border transition-all ${showKeys[key.id] ? "bg-amber-50 border-amber-100 text-amber-600" : "bg-white border-gray-100 text-gray-400 hover:text-gray-900 hover:border-gray-200"
                              }`}
                          >
                            {showKeys[key.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button
                            onClick={() => deleteKey(key.id)}
                            className="p-2.5 bg-white border border-gray-100 text-gray-400 hover:text-red-600 hover:bg-red-50 hover:border-red-100 rounded-xl transition-all"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>


      </div>
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Delete Neural Webhook?"
        message="This action will permanently terminate this data protocol. Are you prepared to proceed with the deletion?"
      />
    </div>
  );
};

export default WebhookManagement;
